import { authSessionStorage } from "~/utils/helpers/auth/browser-context";
import { callWithNuxt } from "#app";
import { SHA3 } from "sha3";
import { storeToRefs } from "pinia";
import { useAuthStore } from "~/store/auth/auth.store";
import { useRequestsStore } from "~/store/requests/requests.store";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { CONNEXION_STEPS } from "~/utils/enums/auth.enums";
import {
  clearStoredReturnTo,
  isBusinessReturnToAlias,
  readStoredReturnTo,
  resolvePostLoginDestination,
  selectReturnTo,
  storeReturnTo,
} from "~/utils/helpers/auth/redirect";

export const useAuth = () => {
  function hashPassword(password: string) {
    const hash = new SHA3(512);
    hash.update(password);
    return hash.digest("hex");
  }

  const router = useRouter();
  const getOrigin = () => (import.meta.client ? window.location.origin : "http://localhost");
  const getStorage = () => (import.meta.client ? authSessionStorage() : undefined);

  function saveReturnTo(value?: string): string | undefined {
    const storage = getStorage();
    if (!storage) {
      return undefined;
    }

    if (value === undefined) {
      return getReturnTo();
    }

    const selected = selectReturnTo({
      origin: getOrigin(),
      explicit: { provided: true, value },
    });

    return storeReturnTo(storage, selected, getOrigin());
  }

  function getReturnTo(): string | undefined {
    const storage = getStorage();
    if (!storage) {
      return undefined;
    }

    const stored = readStoredReturnTo(storage, getOrigin());
    const selected = selectReturnTo({
      origin: getOrigin(),
      explicit: { provided: false },
      stored,
      ssoRedirect: useCookie("sso-redirect").value,
      redirectUrl: useCookie("redirect_url").value,
    });

    if (selected && !stored) {
      storeReturnTo(storage, selected, getOrigin());
    }

    return selected;
  }

  function clearReturnTo(): void {
    const storage = getStorage();
    if (storage) {
      clearStoredReturnTo(storage);
    }

    useCookie("sso-redirect").value = null;
    useCookie("redirect_url").value = null;
  }

  async function handleExternalRedirect(next_url?: string) {
    const app = useNuxtApp();
    const authStore = useAuthStore();
    const storage = getStorage();
    let returnTo: string | undefined;

    if (next_url !== undefined) {
      returnTo = selectReturnTo({
        origin: getOrigin(),
        explicit: { provided: true, value: next_url },
        stored: storage ? readStoredReturnTo(storage, getOrigin()) : undefined,
        ssoRedirect: useCookie("sso-redirect").value,
        redirectUrl: useCookie("redirect_url").value,
      });

      if (storage) {
        storeReturnTo(storage, returnTo, getOrigin());
      }
    } else {
      returnTo = getReturnTo();
    }

    if (!authStore.isLoggedIn()) {
      return navigateTo("/", { replace: true });
    }

    if (returnTo && isBusinessReturnToAlias(returnTo)) {
      await authStore.fetchProfile();

      if (!authStore.isLoggedIn()) {
        return navigateTo("/", { replace: true });
      }
    }

    const { connexion } = storeToRefs(authStore);
    const profileComplete = !!authStore.user?.firstname && !!authStore.user?.lastname;
    if (!profileComplete) {
      connexion.value.connexionStep = CONNEXION_STEPS.REGISTER_NAME;
    }

    const isAllowed = authStore.isManager || authStore.isCompanyAdmin || authStore.isGroupAdmin;
    const result = await resolvePostLoginDestination({
      returnTo,
      profileComplete,
      userId: authStore.user?.userId ?? "",
      isAllowed,
      isKnownRoute: (destination) => router.resolve(destination).matched.length > 0,
      fetchTrainingPlanOwnerId: async (id) => {
        const sessionStore = useSessionsStore();
        await callWithNuxt(app, sessionStore.fetchTrainingPlanSession, [`tr-${id}`]);
        return sessionStore.trainingPlanSessionIndividual?.userId ?? undefined;
      },
      fetchRequestOwnerId: async (id) => {
        const requestsStore = useRequestsStore();
        await callWithNuxt(app, requestsStore.fetchRequestDetails, [id]);
        return requestsStore.requestDetails?.userId ?? undefined;
      },
    });

    const navigation = await callWithNuxt(app, navigateTo, [result.destination, { replace: true }]);

    if (result.consumeReturnTo) {
      clearReturnTo();
    }

    return navigation;
  }

  function handleRedirect(): any {
    const authStore = useAuthStore();

    if (authStore.isLoggedIn()) {
      return handleExternalRedirect();
    }

    return navigateTo("/");
  }

  return {
    hashPassword,
    handleRedirect,
    handleExternalRedirect,
    saveReturnTo,
    getReturnTo,
    clearReturnTo,
  };
};
